import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Textarea } from '@mantine/core';
import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Input } from 'ui-components/components/input';
import { TaxIdentifiers } from '../../../enums/tax-identifiers/tax-identifiers';
import { TaxIdentifierTypes } from '../../../enums/tax-identifiers/tax-identifiers-types';
import { getTaxIdentifierOptions, getSelectedOptionMessageDescriptor } from '../../../services/tax-identifier-service';
import { useSortedTranslatedCountries } from '../../../hooks/use-sorted-translated-countries';
import IssuingTaxAuthorityDropdownItem from './issuing-authority-dropdown-item';
import TaxIdentifierDropdownItem from './tax-identifier-dropdown-item';
import countryNameMessages from '../../../components/country-names.messages';
import Global from '../../../store/global-selectors';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import messages from './messages';
import asModal from '../../../components/as-modal';

import style from './style.module.less';

export const AddTaxIdentifierModal = ({ onClose, onAdd, taxIdentifiers }) => {
  const [taxIdentifierType, setTaxIdentifierType] = useAsyncSafeState(TaxIdentifierTypes.taxVAT.value);
  const [taxNumber, setTaxNumber] = useAsyncSafeState();
  const [taxNickname, setTaxNickname] = useAsyncSafeState();
  const [taxDescription, setTaxDescription] = useAsyncSafeState();

  const defaultIssuingAuthority = useSelector(Global.selectSellerHomeCountry);
  const [taxIssuingAuthority, setTaxIssuingAuthority] = useAsyncSafeState(defaultIssuingAuthority);

  const [errors, setErrors] = useAsyncSafeState([]);
  const taxIdentifierOptions = getTaxIdentifierOptions(TaxIdentifiers.taxIdentifierType);

  const getSelectedTaxIdentifierTypeMessageDescriptor = useCallback(
    () => getSelectedOptionMessageDescriptor(taxIdentifierType, taxIdentifierOptions),
    [taxIdentifierType, taxIdentifierOptions],
  );

  const emptyMessageReplacement = <div style={{ height: '14px' }} />;

  const onAddClicked = useCallback(() => {
    setErrors([]);
    const isNameUnique = !taxIdentifiers.find((identifier) => identifier.name === taxNickname);
    if (taxIdentifierType && taxNumber && taxNickname && isNameUnique) {
      const taxIdentifierData = {
        taxNumber,
        type: taxIdentifierType,
        issuingAuthority: taxIssuingAuthority,
        name: taxNickname,
        description: taxDescription,
      };
      try {
        onAdd(taxIdentifierData);
      } catch {
        setErrors(['addError']);
      }
    } else {
      setErrors([...(!isNameUnique ? ['uniqueName'] : []), ...(!taxNumber ? ['taxNumber'] : []), ...(!taxNickname ? ['taxNickname'] : [])]);
    }
  }, [taxIdentifierType, taxNumber, taxIssuingAuthority, taxNickname, taxDescription, setErrors, taxIdentifiers, onAdd]);

  const handleSetTaxIdentifierType = useCallback(
    (typeOption) => {
      setTaxIdentifierType(typeOption);
    },
    [setTaxIdentifierType],
  );

  const handleSetIssuingAuthority = useCallback(
    (issuingAuthorityOption) => {
      setTaxIssuingAuthority(issuingAuthorityOption);
    },
    [setTaxIssuingAuthority],
  );

  const sortedTranslatedCountryObjs = useSortedTranslatedCountries();
  const handleSetTaxDescription = useCallback(
    (e) => {
      setTaxDescription(e.target.value);
    },
    [setTaxDescription],
  );

  const shouldDisableButton = !(taxIdentifierType && taxNumber && taxNickname && taxIssuingAuthority);

  const pendoTrackingId = '/settings/international#add-tax-identifier';

  return (
    <Modal className={style['add-tax-id-modal']}>
      <ModalHeader closeModal={onClose}>
        <FormattedMessage {...messages.addATaxIdentifier} />
      </ModalHeader>
      <ModalBody className={style['modal-body']}>
        <div className={style['add-tax-id-grid']}>
          <div className={style.row}>
            <div className={style['tax-setting']}>
              <div className={style['tax-setting-label']}>
                <FormattedMessage {...TaxIdentifiers.taxIdentifierType.messageDescriptor} />
              </div>
              <div className={style['tax-type-options']}>
                <DropdownMenu className={style.dropdown} dropdownMenuShouldHaveWidthOfButton>
                  <DropdownMenuToggler className={style['dropdown-selector']} required theme='white'>
                    {!getSelectedTaxIdentifierTypeMessageDescriptor() ? (
                      emptyMessageReplacement
                    ) : (
                      <FormattedMessage {...getSelectedTaxIdentifierTypeMessageDescriptor()} />
                    )}
                  </DropdownMenuToggler>
                  {taxIdentifierOptions.map((option) => (
                    <TaxIdentifierDropdownItem
                      key={option.value}
                      onClick={handleSetTaxIdentifierType}
                      option={option}
                      selected={taxIdentifierType === option.value}
                    />
                  ))}
                </DropdownMenu>
              </div>
            </div>
          </div>
          <div className={style.row}>
            <div className={style['tax-setting']}>
              <div className={style['tax-setting-label']}>
                <FormattedMessage {...TaxIdentifiers.taxIssuingAuthority.messageDescriptor} />
              </div>
              <div className={style['tax-setting-options']}>
                <DropdownMenu className={style.dropdown} dropdownMenuShouldHaveWidthOfButton>
                  <DropdownMenuToggler className={style['dropdown-selector']} required theme='white'>
                    <FormattedMessage {...countryNameMessages[taxIssuingAuthority]} />
                  </DropdownMenuToggler>
                  {sortedTranslatedCountryObjs.map((option) => (
                    <IssuingTaxAuthorityDropdownItem
                      key={option.code}
                      onClick={handleSetIssuingAuthority}
                      option={option}
                      selected={taxIssuingAuthority === option.code}
                    />
                  ))}
                </DropdownMenu>
              </div>
            </div>
          </div>
          <div className={style.row}>
            <div className={style['tax-setting']}>
              <div className={style['tax-setting-label']}>
                <FormattedMessage {...TaxIdentifiers.taxNumber.messageDescriptor} />
                <span className={style['required-label']}> &lowast;</span>
              </div>
              <Input
                className={style.input}
                hasError={!taxNumber && errors.includes('taxNumber')}
                onChange={setTaxNumber}
                value={taxNumber}
              />
            </div>
          </div>
          <div className={style.row}>
            <div className={style['tax-setting']}>
              <div className={style['tax-setting-label']}>
                <FormattedMessage {...TaxIdentifiers.taxNickname.messageDescriptor} />
                <span className={style['required-label']}> &lowast;</span>
              </div>
              <Input
                className={style.input}
                hasError={!taxNickname && errors.includes('taxNickname')}
                onChange={setTaxNickname}
                type='text'
                value={taxNickname}
              />
            </div>
            {errors.includes('uniqueName') && (
              <div className={style['unique-name-error']}>
                <FormattedMessage {...messages.uniqueNickname} />
              </div>
            )}
          </div>
          <div className={style.row}>
            <div className={style['tax-setting']}>
              <div className={style['flex-label']}>
                <div className={style.bold}>
                  <FormattedMessage {...TaxIdentifiers.taxDescription.messageDescriptor} />
                </div>
                <div>
                  <FormattedMessage {...messages.optional} />
                </div>
              </div>
              <Textarea autosize minRows={4} onChange={handleSetTaxDescription} value={taxDescription} />
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className={style['modal-footer']}>
        <Button onClick={onClose} theme='tertiary'>
          <FormattedMessage {...messages.cancel} />
        </Button>
        <Button disabled={shouldDisableButton} onClick={onAddClicked} pendoTrackingId={pendoTrackingId} theme='primary'>
          <FormattedMessage {...messages.addTaxIdentifierButton} />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddTaxIdentifierModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  taxIdentifiers: PropTypes.array.isRequired,
};

export default asModal(AddTaxIdentifierModal);
